import { useState, useMemo } from "react";
import { useMediaQuery } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  formatDate,
  isWithinLastWeek,
  IsWithinLastMonth,
} from "../../../shared/Utils/DateFilters";
import { FontAwesomeIcon as FA } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};
fuzzyFilter.autoRemove = (val) => !val;

export default function UserDonationTable({ data, npData, serverPageCount = 1, serverTotalCount, onServerPageChange }) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [timeRange, setTimeRange] = useState("all");
  const [expandRow, setExpandRow] = useState(null);

  const matchesMax430px = useMediaQuery("(max-width: 430px)");

  const timeFilteredData = useMemo(() => {
    return data.filter((d) => {
      const date = new Date(d.created_at);
      if (timeRange === "week") return isWithinLastWeek(date);
      if (timeRange === "month") return IsWithinLastMonth(date);
      return true;
    });
  }, [data, timeRange]);

  const columns = useMemo(() => [
    { accessorKey: "nonprofit_name", header: "Nonprofit Name" },
    { accessorKey: "campaign_title", header: "Campaign Title" },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ getValue }) => `$${getValue()}`,
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: ({ getValue }) => formatDate(getValue()),
      enableGlobalFilter: false,
    },
    {
      id: "payment_id",
      accessorFn: (row) => row.payment_intent?.id ?? "",
      header: "Payment ID",
      enableGlobalFilter: false,
    },
  ], []);

  const handlePaginationChange = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    if (next.pageIndex !== pagination.pageIndex) {
      onServerPageChange?.(next.pageIndex + 1);
    }
    setPagination(next);
  };

  const table = useReactTable({
    data: timeFilteredData,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: handlePaginationChange,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: serverPageCount,
  });

  const totalCount = serverTotalCount ?? table.getFilteredRowModel().rows.length;
  const { pageIndex } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const currentRows = table.getRowModel().rows;

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8">
      <div className="p-6">
        <h2 className="text-orange !text-3xl font-bold text-center mb-4">
          Your Donations
        </h2>

        {/* Search + time filter */}
        <div className="flex flex-col md:flex-row gap-2 mb-6">
          <div className="flex items-center gap-3 border border-gray-300 rounded-full px-4 py-[1px] flex-1">
            <FA icon={faMagnifyingGlass} className="text-gray-400" size="sm" />
            <input
              type="text"
              placeholder="Search by Nonprofit Name or Campaign Title"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setPageIndex(0);
              }}
              className="flex-1 outline-none border-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
            />
          </div>
          <select
            value={timeRange}
            onChange={(e) => {
              setTimeRange(e.target.value);
              table.setPageIndex(0);
            }}
            className="border border-gray-300 rounded-full px-4 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:border-gray-400"
          >
            <option value="all">All Time</option>
            <option value="month">This Month</option>
            <option value="week">This Week</option>
          </select>
        </div>

        {matchesMax430px ? (
          /* Mobile: 3-column compact with expandable rows */
          <table className="w-full table-fixed border-separate border-spacing-y-1">
            <thead>
              <tr>
                <th className="text-sm font-medium text-neutral-500 align-top text-left px-2 py-2">Nonprofit Name</th>
                <th className="text-sm font-medium text-neutral-500 align-top text-left px-2 py-2">Campaign Title</th>
                <th className="text-sm font-medium text-neutral-500 align-top text-left px-2 py-2">Amount</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map((row) => {
                const d = row.original;
                const isExpanded = expandRow === row.id;
                return (
                  <tr
                    key={row.id}
                    className="bg-sky-50 cursor-pointer"
                    onClick={() => setExpandRow(isExpanded ? null : row.id)}
                  >
                    <td className="rounded-l-xl text-neutral-600 text-sm px-2 py-2 break-words">
                      {d.nonprofit_name}
                      {isExpanded && (
                        <div className="mt-2">
                          <p className="text-gray-500 text-xs">Date</p>
                          <span className="text-neutral-600 text-xs font-bold">{formatDate(d.created_at)}</span>
                        </div>
                      )}
                    </td>
                    <td className="text-neutral-600 text-sm text-center px-2 py-2">
                      {d.campaign_title}
                      {isExpanded && (
                        <div className="mt-2">
                          <p className="text-gray-500 text-xs">Payment ID</p>
                          <span className="text-neutral-600 text-xs font-bold break-all">{d.payment_intent?.id}</span>
                        </div>
                      )}
                    </td>
                    <td className="text-neutral-600 text-sm text-center px-2 py-2">${d.amount}</td>
                    <td className="rounded-r-xl text-center px-2 py-2">
                      <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        size="sm"
                        className="text-gray-400"
                      />
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={4} className="text-center text-sm text-gray-400 py-8">No donations found.</td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          /* Desktop: full table with sorting */
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50">
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          className={`px-3 py-3 text-left border border-gray-200 !text-sm !font-semibold text-orange whitespace-nowrap ${canSort ? "cursor-pointer select-none" : ""}`}
                        >
                          <span className="flex items-center gap-1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <span className={`ml-1 ${sorted ? "text-orange" : "text-orange opacity-30"}`}>
                                {sorted === "asc" ? "↑" : sorted === "desc" ? "↓" : "↑↓"}
                              </span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {currentRows.length > 0 ? currentRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 border border-gray-200 !text-sm text-gray-800 text-left">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={columns.length} className="py-8 text-center text-sm text-gray-400 border border-gray-200">
                      No donations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <p className="!text-sm text-gray-500">Total Donations: {totalCount}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-40"
            >
              <FA icon={faChevronLeft} size="xs" />
            </button>
            <span className="!text-sm text-gray-600">
              {pageIndex + 1} of {Math.max(1, pageCount)}
            </span>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-40"
            >
              <FA icon={faChevronRight} size="xs" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
